import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { passosVisiveis, type PassoDef } from '../config/primeirosPassos';
import OnboardingStatusService, { type MapaSinais } from '../services/OnboardingStatusService';

/**
 * O estado de `/primeiros-passos`, compartilhado entre a TELA e o CONTADOR da
 * pílula no rodapé da sidebar.
 *
 * Existe por dois motivos, nessa ordem:
 *
 * 1. **Não medir duas vezes.** Sem isto, abrir a tela dispararia as oito
 *    leituras de novo, logo depois de a sidebar já tê-las feito.
 * 2. **O contador precisa do número antes de a tela ser aberta** — ele é o
 *    convite. Ler só na tela faria o "0/8" aparecer depois de entrar nela,
 *    que é quando ele não serve mais para nada.
 *
 * Substituiu o `OnboardingProvider` do tour, apagado no mesmo commit.
 */

interface EstadoPrimeirosPassos {
    visiveis: PassoDef[];
    sinais: MapaSinais;
    carregando: boolean;
    concluidos: number;
    total: number;
    /** Nova medição. A tela chama ao montar (volta de `/products` com produto
        recém-cadastrado tem que atualizar); chamadas concorrentes são ignoradas. */
    remedir: () => void;
}

const Ctx = createContext<EstadoPrimeirosPassos | undefined>(undefined);

/* Lembra se a última medição deu tudo pronto. Serve só para a carga do app não
   gastar oito requisições num contador que não vai aparecer — a tela, quando
   aberta, mede de qualquer jeito. Chave por usuário para não vazar o estado de
   uma conta para outra no mesmo navegador. */
const CHAVE_CACHE = 'brik_primeiros_passos_prontos';

const idDoUsuario = (): string => {
    try {
        const u = JSON.parse(localStorage.getItem('user') || '{}');
        return String(u?.userId ?? u?.id ?? u?.email ?? 'anon');
    } catch {
        return 'anon';
    }
};

const leuTudoProntoAntes = (): boolean => {
    try {
        return localStorage.getItem(CHAVE_CACHE) === idDoUsuario();
    } catch {
        return false;
    }
};

const guardarTudoPronto = (pronto: boolean) => {
    try {
        if (pronto) localStorage.setItem(CHAVE_CACHE, idDoUsuario());
        else localStorage.removeItem(CHAVE_CACHE);
    } catch {
        /* Navegador sem storage: só perde a economia, nada quebra. */
    }
};

export const PrimeirosPassosProvider = ({ children }: { children: ReactNode }) => {
    const visiveis = useMemo(() => passosVisiveis(), []);
    const [sinais, setSinais] = useState<MapaSinais>({});
    const [carregando, setCarregando] = useState(false);
    /* O cache diz "estava tudo pronto" e por isso a carga não mede. Sem isto o
       contador ficava em 0 de N, e a sidebar mostrava a conta pronta como se não
       tivesse feito nada ("0/8" em destaque). Vale até a primeira medição. */
    const [prontoPeloCache] = useState(leuTudoProntoAntes);
    const medindo = useRef(false);
    const vivo = useRef(true);

    useEffect(() => () => { vivo.current = false; }, []);

    const remedir = useCallback(() => {
        if (medindo.current || visiveis.length === 0) return;
        medindo.current = true;
        setCarregando(true);

        OnboardingStatusService.medir(visiveis.map(p => p.id))
            .then(medido => {
                if (!vivo.current) return;
                setSinais(medido);
                guardarTudoPronto(visiveis.every(p => medido[p.id]?.feito));
            })
            .finally(() => {
                medindo.current = false;
                if (vivo.current) setCarregando(false);
            });
    }, [visiveis]);

    useEffect(() => {
        if (!leuTudoProntoAntes()) remedir();
    }, [remedir]);

    const mediu = Object.keys(sinais).length > 0;
    const concluidos = !mediu && prontoPeloCache
        ? visiveis.length
        : visiveis.filter(p => sinais[p.id]?.feito).length;

    const valor: EstadoPrimeirosPassos = {
        visiveis,
        sinais,
        carregando,
        concluidos,
        total: visiveis.length,
        remedir,
    };

    return <Ctx.Provider value={valor}>{children}</Ctx.Provider>;
};

export const usePrimeirosPassos = (): EstadoPrimeirosPassos => {
    const ctx = useContext(Ctx);
    if (!ctx) throw new Error('usePrimeirosPassos precisa estar dentro de PrimeirosPassosProvider');
    return ctx;
};
